/**
 * ATL-1445 stage 3, P4 — the session write-mode report: `write_mode_pinned`.
 *
 * The seam pins each session's write mode at startup (`global/Write/writeW.ts`). Two things about
 * that pin must be countable rather than inferred, and both are stage-3 requirements:
 *
 *   - a bundle that cannot honour a Supabase-master organization pins REFUSE (decision S1 of #929;
 *     until ATL-1601's interceptor ships its unconverted writers still write Firestore-first, i.e. it
 *     is a second master of the books). The `write-bundle-coverage` gate clause exists so there are
 *     none; this event is how one that slipped through is found, by organization AND bundle (the
 *     running-bundle properties below), instead of by a drift report weeks later;
 *   - §4: the invalid state `writes = supabase while reads = firestore` is "never entered, and
 *     alerted". The alert is this event.
 *
 * It also answers the question ATL-887 taught this project to ask of every flip: not "was the flag
 * set", but "how many tills acted on it". A flip reaches a device only through its next pin.
 *
 * Emitted once per pin CHANGE of the process (`pinned`, `replaced-organization`) and once per refused
 * re-pin (`kept-divergent` — the flag moved under a running session, which only a reload applies).
 * An unchanged re-pin (`kept`) and `not-pinned` emit nothing. At most a few events per app start.
 *
 * No PII: the organization's Firestore id (the PostHog `organization` group key), enums, booleans.
 */
import { captureOperationalEvent, type OperationalProperties } from './operationalTelemetry'
import { runningBundleProperties } from './writeBundleCapability'
import type { LastKnownWritesMaster, WritesMasterObservation, WriteModeAlert } from '../Write/writeMode'
import type { SessionWriteModePinOutcome } from '../Write/writeW'

export const WRITE_MODE_PINNED_EVENT = 'write_mode_pinned'

/** Alerts that mean a second master or a refused till is possible: logged as errors. */
const SEVERE: ReadonlySet<WriteModeAlert> = new Set<WriteModeAlert>([
  'incapable-bundle-on-supabase-master-org',
  'writes-supabase-without-reads',
  // P5: master writes still on this device while the organization is Firestore-master.
  'outbox-v2-not-drained',
])

export const describeObservation = (o: WritesMasterObservation): string =>
  o.kind === 'read' ? `${o.provenance}:${o.value}` : `unreadable:${o.why}`

export const describeLastKnown = (l: LastKnownWritesMaster): string =>
  l.status === 'known' ? `known:${l.value}` : l.status

/** The event's properties, or null when this outcome is not reported. */
export const buildWriteModePinnedProperties = (outcome: SessionWriteModePinOutcome): OperationalProperties | null => {
  const { status, pin, decision } = outcome
  if (!decision || !pin || status === 'kept' || status === 'not-pinned') return null
  const inForce = pin.decision
  return {
    organization_id: decision.organizationId,
    pin_status: status,
    // The mode `writeW` obeys now. For `kept-divergent` that is the EARLIER pin, not this decision.
    write_mode: inForce.mode,
    write_mode_reason: inForce.reason,
    decided_write_mode: decision.mode,
    decided_write_mode_reason: decision.reason,
    writes_master: decision.resolution.writesMaster,
    writes_master_basis: decision.resolution.basis,
    writes_master_observation: describeObservation(decision.resolution.observation),
    writes_master_last_known: describeLastKnown(decision.resolution.lastKnown),
    session_reads_supabase: decision.sessionReadsSupabase,
    write_w_capable: decision.capability.capable,
    write_w_seam_version: decision.capability.seamVersion,
    write_w_reason: decision.capability.reason,
    write_mode_alerts: [...decision.alerts],
    // P5: what startup knew about outbox v2 (`not-checked` for every organization today).
    write_mode_outbox_v2: decision.outboxV2,
    // The RUNNING bundle, in the keys `ota-coverage` and `write_w_capability_reported` join on — set
    // on the event so a persisted super property from another bundle cannot stand in for them.
    ...runningBundleProperties(),
  }
}

/**
 * Report a pin outcome: one operational event, plus a console line a human reads at startup. Never
 * throws — telemetry must not be able to break the startup it rides on.
 *
 * @returns whether an event was captured.
 */
export const reportSessionWriteMode = (
  outcome: SessionWriteModePinOutcome,
  logger: Pick<Console, 'error' | 'warn'> = console,
): boolean => {
  try {
    const properties = buildWriteModePinnedProperties(outcome)
    if (!properties) return false
    const alerts = outcome.decision?.alerts ?? []
    const severe = outcome.status === 'kept-divergent' || alerts.some((a) => SEVERE.has(a))
    if (severe) logger.error('[write-mode] session write mode', properties)
    else if (alerts.length > 0) logger.warn('[write-mode] session write mode', properties)
    captureOperationalEvent(WRITE_MODE_PINNED_EVENT, properties)
    return true
  } catch {
    return false
  }
}
